import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const StrategicDomainsSection = () => {
  const domains = [
    {
      title: 'Formation Professionnelle',
      stat: '500+ Professionnels',
      description: 'Développement des compétences',
      link: '/professional-training-services',
      details: 'Programmes de formation certifiés conçus pour développer les compétences professionnelles et techniques des équipes africaines.',
      features: [
        'Programmes Certifiés',
        'Formation Digitale',
        'Accompagnement Personnalisé',
        'Suivi Post-Formation'
      ]
    },
    {
      title: 'Conseil Stratégique',
      stat: 'Programmes Certifiés',
      description: 'Accompagnement stratégique',
      link: '/strategic-consulting-services',
      details: 'Solutions de conseil stratégique adaptées aux défis uniques des organisations africaines.',
      features: [
        'Diagnostic Organisationnel',
        'Planification Stratégique',
        'Gestion du Changement',
        'Optimisation des Processus'
      ]
    },
    {
      title: 'Intelligence Artificielle',
      stat: 'Innovation Technologique',
      description: 'Solutions IA innovantes',
      link: '/ai-solutions-integration-hub',
      details: 'Intégration de solutions d\'intelligence artificielle de pointe pour moderniser les opérations.',
      features: [
        'Automatisation Intelligente',
        'Analyse Prédictive',
        'IA Conversationnelle',
        'Machine Learning'
      ]
    },
    {
      title: 'Études & Recherches',
      stat: '20+ Pays Africains',
      description: 'Recherche appliquée',
      link: '/research-studies-services',
      details: 'Études approfondies et recherches spécialisées pour éclairer les décisions stratégiques.',
      features: [
        'Études de Marché',
        'Recherche Sectorielle',
        'Analyse Comparative',
        'Publications Scientifiques'
      ]
    },
    {
      title: 'Marketing Digital',
      stat: 'Impact Mesurable',
      description: 'Stratégies digitales',
      link: '/digital-marketing-services',
      details: 'Stratégies de marketing digital complètes pour renforcer la présence en ligne.',
      features: [
        'Stratégie de Contenu',
        'SEO & Analytics',
        'Réseaux Sociaux',
        'Campagnes Publicitaires'
      ]
    },
    {
      title: 'Management de Projets',
      stat: 'Excellence Livrée',
      description: 'Gestion de projets',
      link: '/digital-project-management-services',
      details: 'Gestion professionnelle de projets numériques avec méthodologies agiles.',
      features: [
        'Méthodologies Agiles',
        'Gestion des Risques',
        'Coordination d\'Équipes',
        'Livraison Continue'
      ]
    },
    {
      title: 'Management des RH',
      stat: 'Talents Optimisés',
      description: 'Gestion RH stratégique',
      link: '/human-resources-management-services',
      details: 'Solutions complètes de gestion des ressources humaines pour développer et retenir les talents.',
      features: [
        'Recrutement Stratégique',
        'Développement des Talents',
        'Performance Management',
        'Culture Organisationnelle'
      ]
    },
    {
      title: 'Thérapie d\'Entreprise',
      stat: 'Transformation Profonde',
      description: 'Guérison organisationnelle',
      link: '/business-therapy-services',
      details: 'Approche unique de thérapie organisationnelle pour résoudre les dysfonctionnements profonds.',
      features: [
        'Diagnostic Approfondi',
        'Interventions Ciblées',
        'Accompagnement Long Terme',
        'Transformation Culturelle'
      ]
    }
  ];

  const [selectedDomain, setSelectedDomain] = useState(0);

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
          >
            Accélérez la Transformation de <span className="text-[#96121D]">Votre Organisation</span>
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-lg text-gray-600 max-w-3xl mx-auto"
          >
            Huit domaines d'excellence pour propulser votre organisation vers les standards internationaux
          </motion.p>
        </div>

        {/* Content Grid */}
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Navigation Tabs */}
          <div className="lg:col-span-4 space-y-2">
            {domains?.map((domain, index) => (
              <button
                key={index}
                onClick={() => setSelectedDomain(index)}
                className={`w-full text-left px-6 py-4 rounded-xl transition-all duration-300 ${
                  selectedDomain === index
                    ? 'bg-[#96121D] text-white shadow-lg'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
              >
                <div className="font-semibold text-base mb-1">{domain?.title}</div>
                <div className="text-sm opacity-80">{domain?.stat}</div>
              </button>
            ))}
          </div>

          {/* Content Display */}
          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedDomain}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-gray-50 rounded-2xl p-8 md:p-10"
              >
                {/* Title */}
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  {domains?.[selectedDomain]?.title}
                </h3>

                {/* Description */}
                <p className="text-gray-600 text-lg leading-relaxed mb-8">
                  {domains?.[selectedDomain]?.details}
                </p>

                {/* Features */}
                <div className="mb-8">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
                    Points Clés
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {domains?.[selectedDomain]?.features?.map((feature, i) => (
                      <div key={i} className="flex items-center text-gray-700">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#96121D] mr-3" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href={domains?.[selectedDomain]?.link}
                    className="inline-flex items-center justify-center px-6 py-3 bg-[#96121D] text-white font-semibold rounded-lg hover:bg-[#7a0e17] transition-colors"
                  >
                    En savoir plus
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                    </svg>
                  </a>
                  <a
                    href="/information-request-form"
                    className="inline-flex items-center justify-center px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:border-[#96121D] hover:text-[#96121D] transition-colors"
                  >
                    Demander un devis
                  </a>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20 bg-gray-900 rounded-2xl p-8 md:p-12 text-center"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Besoin d'un accompagnement sur mesure ?
          </h3>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Nos experts sont à votre disposition pour analyser vos besoins et concevoir une solution adaptée
          </p>
          <a
            href="/contact"
            className="inline-flex items-center justify-center px-8 py-4 bg-[#9B8E57] text-white font-semibold rounded-lg hover:bg-[#8a7a4a] transition-colors"
          >
            Contactez-nous
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default StrategicDomainsSection;